<template lang="pug">
div
	mapbox(
		ref="map"
		style="height:500px"
		:layers="layers"
		:sources="sources"
		v-on:map-load="mapLoaded"
		@map-styleimagemissing="styleImageMissing"
		@map-styleload="mapStyle"
		:fitbounds="features.map(e=>e.geometry.coordinates)")
		div(slot="popup")
			nuxt-link(:to="detailPage(popupData)" @click="$gtag.event('click', {event_label:'acco-map-popup', event_category: 'accommodations-search'})")
				v-container.pa-2
					v-row(cols="12")
						v-col(v-if="popupData.image").pa-0
							v-img.mb-2(:src="getThumb(popupData.image)" width="250")
					v-row(cols="12")
						v-col(v-if="popupData.name").pa-0
							h4 {{popupData.name}}
					v-row(cols="12")
						v-col(v-if="popupData.price").pa-0
							.accent--text.font-weight-bold {{ $n(Math.ceil(popupData.price), 'simplecurrency', $i18n.locale) }}
						v-col(v-if="popupData.rating").pa-0.shrink
							v-chip.px-1.white--text(label small :color="ratingcolor(popupData.rating)") {{popupData.rating < 10 ? popupData.rating.toFixed(1): 10}}
</template>

<script>
import gql from 'graphql-tag';
import debounce from 'lodash/debounce';

import {
	decodeFilters,
	encodeFilters
} from '@dengl/common/utils/filterstrings';

const query = gql`
	query Accommodations(
		$filters: String
		$limit: Int
		$sort: AccommodationSortInput
	) {
		accommodations(filters: $filters, limit: $limit, sort: $sort) {
			total
			filters
			nodes {
				id
				name
				type
				images
				location
				rating
				variants {
					price
					date
				}
			}
		}
	}
`;

const roundRect = (ctx, x, y, width, height) => {
	let radius = 6;
	radius = { tl: radius, tr: radius, br: radius, bl: radius };

	ctx.beginPath();
	ctx.moveTo(x + radius.tl, y);
	ctx.lineTo(x + width - radius.tr, y);
	ctx.quadraticCurveTo(x + width, y, x + width, y + radius.tr);
	ctx.lineTo(x + width, y + height - radius.br);
	ctx.quadraticCurveTo(
		x + width,
		y + height,
		x + width - radius.br,
		y + height
	);
	ctx.lineTo(x + radius.bl, y + height);
	ctx.quadraticCurveTo(x, y + height, x, y + height - radius.bl);
	ctx.lineTo(x, y + radius.tl);
	ctx.quadraticCurveTo(x, y, x + radius.tl, y);
	ctx.closePath();

	ctx.fill();
	// ctx.stroke();
};

export default {
	name: 'AccommodationMap',
	components: {
		Mapbox: () => (process.client ? import('@/components/mapbox.vue') : null)
	},
	props: {
		filterstring: {
			type: String,
			default: ''
		},
		limit: {
			type: [String, Number],
			default: 50
		},
		sort: {
			type: Object,
			default() {
				return {
					ascending: true,
					field: 'PRICE'
				};
			}
		}
	},
	data() {
		return {
			accommodations: [],
			total: 0,
			fetchPending: true,
			filterStringChangedWhileFetching: false,
			popupData: {}
		};
	},
	async fetch() {
		await this.$graphClient
			.query({
				query,
				variables: {
					filters: this.filterstring,
					limit: parseInt(this.limit),
					sort: this.sort,
					preferedCity: this.$store.getters.preferedCity
				}
			})
			.then(({ data }) => {
				if (data.accommodations.filters === this.filterstring) {
					this.accommodations = data.accommodations.nodes.filter(
						acco => acco.variants.length
					);

					this.fetchPending = false;
				} else if (this.filterStringChangedWhileFetching) {
					this.filterStringChangedWhileFetching = false;
					this.debouncedFetch();
				}
			});
	},
	computed: {
		sortfield() {
			switch (this.sort.field) {
				case 'PRICE':
					return ['variants[0].price'];
				case 'RATING':
					return ['rating'];
				case 'DISTANCE_SKILIFT':
					return ['skilifts.distance'];
				default:
					return [];
			}
		},
		sources() {
			return {
				'map-accommodations': {
					type: 'geojson',
					data: {
						type: 'FeatureCollection',
						features: this.features
					}
				}
			};
		},
		layers() {
			return [
				{
					id: 'map-accommodations',
					type: 'symbol',
					source: 'map-accommodations',
					defaultevents: true,
					layout: {
						// visibility: 'visible',
						'text-field': ['get', 'name'],
						'icon-text-fit': 'both',
						'icon-image': ['get', 'image-name'],
						'icon-allow-overlap': true,
						'text-allow-overlap': true
					}
				}
			];
		},
		features() {
			return this.accommodations.map((accommodation, index) => {
				return {
					type: 'Feature',
					id: index + 10,
					geometry: {
						coordinates: [
							accommodation.location.lon,
							accommodation.location.lat
						],
						type: 'Point'
					},
					properties: {
						...accommodation,
						popupData: {
							id: accommodation.id,
							name: accommodation.name,
							image: accommodation.images.length ? accommodation.images[0] : '',
							price: accommodation.variants[0].price,
							rating: accommodation.rating,
							date: accommodation.variants[0].date
						},
						'image-name': `price-marker|${accommodation.id}|${accommodation.variants[0].price}`,
						name: ''
					}
				};
			});
		}
	},
	watch: {
		filterstring() {
			if (this.fetchPending) {
				this.filterStringChangedWhileFetching = true;
			}
			this.fetchPending = true;
			this.debouncedFetch();
		},
		sort() {
			this.fetchPending = true;
			this.debouncedFetch();
		},
		features() {
			this.$nextTick(() => {
				this.$refs.map.fitToCurrentBounds();
				if (this.accommodations.length === 1) this.$refs.map.setZoom(12);
			});
		}
	},
	methods: {
		styleImageMissing(event) {
			const id = event.missing.id;

			if (id.indexOf('price-marker') !== 0) return;
			const idSplit = id.split('|');

			const price = this.$n(
				Math.ceil(idSplit[2]),
				'simplecurrency',
				this.$i18n.locale
			);

			const canvas = document.createElement('canvas');
			const ctx = canvas.getContext('2d');

			const height = 20;
			const rightWidth = 8 * price.length + 10;

			// bg
			ctx.fillStyle = '#fff';
			roundRect(ctx, 0, 0, rightWidth, height);

			// price
			ctx.textAlign = 'center';
			ctx.font = '13px Arial';
			ctx.fillStyle = '#000';
			ctx.fillText(price, rightWidth / 2, 15);

			// extract data and create mapbox image
			const imageData = ctx.getImageData(0, 0, rightWidth, height);
			event.map.addImage(id, imageData);
		},
		mapLoaded(map) {
			map.loadImage(
				'https://docs.mapbox.com/mapbox-gl-js/assets/popup.png',
				(error, image) => {
					if (error) throw error;
					map.addImage('popup', image, {
						stretchX: [
							[25, 55],
							[85, 115]
						],
						stretchY: [[25, 100]],
						content: [25, 25, 115, 100],
						pixelRatio: 2
					});
				}
			);
		},
		getThumb(source) {
			if (!source) return '';
			const url = new URL(source);
			url.searchParams.append('width', 250);
			const imageWithoutProtocol = url.toString().replace(/https?:\/\//i, '');
			return `https://cdn.wintersport.nl/proxy/${imageWithoutProtocol}`;
		},
		ratingcolor(rating) {
			if (rating >= 9) {
				return '#1f7a45';
			}
			if (rating >= 8) {
				return '#29a35c';
			}
			if (rating >= 7) {
				return 'success';
			}
			if (rating >= 6) {
				return 'warning';
			}
			return '#999999';
		},
		detailPage() {
			if (!this.popupData) return '';
			const filters = decodeFilters(this.filterstring, this.$i18n.locale);
			const hash = encodeFilters(
				{
					date: this.popupData.date,
					...filters
				},
				this.$i18n.locale
			);
			return this.localePath({
				name: 'accommodations-search-id',
				params: { id: this.popupData.id },
				hash: `#${hash}`
			});
		},
		mapStyle(map) {
			map.on('click', 'map-accommodations', e => {
				if (e.features.length > 0) {
					if (e.features[0].id) {
						this.popupData = JSON.parse(e.features[0].properties.popupData);
						this.$refs.map.showPopup(
							e.features[0].geometry.coordinates,
							e.features[0].properties.popup
						);
					}
				}
			});
			map.on('mouseenter', 'map-accommodations', () => {
				map.getCanvas().style.cursor = 'pointer';
			});
			map.on('mouseleave', 'map-accommodations', () => {
				map.getCanvas().style.cursor = '';
			});
		},
		debouncedFetch: debounce(function () {
			this.fetchPending = true;
			this.$fetch();
		}, 200)
	}
};
</script>
